import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Sun, Moon, Globe } from 'lucide-react';
import { Button } from './ui/button';
import logoLight from '@/assets/images/logo/yidm_bglg.png';
import logoDark from '@/assets/images/logo/yidm_logo.png';

export default function Header() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // theme logic preserved (localStorage + html.dark)
  const getInitialTheme = () => {
    try {
      const s = localStorage.getItem('theme');
      if (s) return s;
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch (e) { }
    return 'light';
  };

  const [theme, setTheme] = useState(getInitialTheme);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // logo crossfade helpers (preserve your approach)
  const currentLogoRef = useRef(theme === 'dark' ? logoDark : logoLight);
  const [prevLogo, setPrevLogo] = useState(null);

  useEffect(() => {
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    try { localStorage.setItem('theme', theme); } catch (e) { }

    const newLogo = theme === 'dark' ? logoDark : logoLight;
    if (currentLogoRef.current !== newLogo) {
      setPrevLogo(currentLogoRef.current);
      currentLogoRef.current = newLogo;
      const id = setTimeout(() => setPrevLogo(null), 250);
      return () => clearTimeout(id);
    }
  }, [theme]);

  const toggleTheme = () => setTheme(prev => (prev === 'light' ? 'dark' : 'light'));

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ja' : 'en';
    i18n.changeLanguage(newLang);
    try { localStorage.setItem('language', newLang); } catch (e) { }
    document.documentElement.lang = newLang;
  };

  const navLinks = [
    { path: '/', label: t('header.home') || 'Home' },
    { path: '/capabilities', label: t('header.capabilities') || 'Capabilities' },
    { path: '/machinery', label: t('header.machinery') || 'Machinery' },
    { path: '/about', label: t('header.about') || 'About' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="site-header sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
      <div className="container mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" aria-label={t('header.home', 'Home')}>
            <div className="flex items-start gap-4">
              <div
                className="flex items-center justify-center rounded-lg flex-shrink-0 bg-background/95"
                style={{
                  width: 56,
                  height: 48,
                  background: theme === 'dark' ? '#fff' : '',
                }}
                aria-hidden
              >
                {prevLogo && (
                  <img
                    src={prevLogo}
                    alt=""
                    draggable="false"
                    className="absolute object-contain transition-opacity duration-200 ease-linear"
                    style={{ width: 40, height: 40, opacity: 1 }}
                  />
                )}

                <img
                  src={currentLogoRef.current}
                  alt={t('header.companyName', 'Yazaki India Diecast Mold')}
                  draggable="false"
                  className="object-contain transition-opacity duration-200 ease-linear"
                  style={{
                    width: 40,
                    height: 40,
                    opacity: prevLogo ? 0 : 1,
                  }}
                />
              </div>

              {/* Company Name + Subtitle */}
              <div className="hidden sm:flex flex-col gap-1">
                <span className="font-bold text-lg text-foreground leading-tight">
                  {t("footer.title")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("footer.tagline")}
                </span>
              </div>

            </div>
          </Link>


          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors relative group ${isActive(link.path) ? 'text-primary' : 'text-foreground hover:text-primary'}`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary transform origin-left transition-transform ${isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
                />
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              className="rounded-full"
              aria-label="Toggle language"
            >
              <Globe className="h-5 w-5" />
              <span className="ml-1 text-xs hidden sm:inline">{i18n.language === 'en' ? 'JP' : 'EN'}</span>
            </Button>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>

            {/* CTA Button */}
            <Button
              asChild
              className="hidden sm:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
            >
              <Link to="/about#contact">{t('header.cta') || 'Contact'}</Link>
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-20 z-50 bg-background shadow-lg border-t border-border animate-fadeIn py-4 px-6">

            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-md text-sm font-medium transition-colors ${isActive(link.path) ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'}`}
                >
                  {link.label}
                </Link>
              ))}
              <Button
                asChild
                className="mt-3 w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Link to="/about#contact" onClick={() => setMobileMenuOpen(false)}>
                  {t('header.cta') || 'Contact'}
                </Link>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
